import { useMemo, useState, useRef } from "react";
import type { Task, PlanEvent } from "../../../../shared/types";

interface ProductivityHeatmapProps {
  readonly tasks: Task[];
  readonly events: PlanEvent[];
}

type ViewMode = "week" | "month";

const DAY_LABELS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const MONTHS = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];

const CELL = 12;
const GAP = 3;

function toDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Composant Cell avec tooltip intégré
interface CellProps {
  readonly date: string;
  readonly count: number;
  readonly future: boolean;
  readonly colorClass: string;
}

function HeatmapCell({ date, count, future, colorClass }: CellProps) {
  const [show, setShow] = useState(false);
  const clickTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getActivityLevel = (count: number): string => {
    if (count === 0) return "Aucune activité";
    if (count <= 2) return "Peu d'activité";
    if (count <= 4) return "Activité modérée";
    if (count <= 6) return "Bonne activité";
    return "Très bonne activité !";
  };

  const fmtDate = new Date(date + "T00:00:00").toLocaleDateString("fr-FR", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });

  const handleToggle = () => {
    // Annuler le timeout de fermeture s'il existe
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
    }
    setShow((prev) => !prev);
  };

  const handleMouseEnter = () => {
    // N'activer par hover que si pas ouvert par clic récent
    setShow(true);
  };

  const handleMouseLeave = () => {
    // Fermer avec un délai pour ne pas interférer avec le clic mobile
    clickTimeoutRef.current = setTimeout(() => {
      setShow(false);
    }, 200);
  };

  if (count === 0 || future) {
    return <div className={`w-full h-full rounded-[2px] ${colorClass}`} style={{ width: `${CELL}px`, height: `${CELL}px` }} />;
  }

  return (
    <div
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className={`w-full h-full rounded-[2px] cursor-pointer hover:ring-2 hover:ring-accent/30 transition-all duration-150 ${colorClass}`}
        style={{ width: `${CELL}px`, height: `${CELL}px` }}
        onClick={handleToggle}
      />
      {show && (
        <div
          className="absolute z-50 bg-surface border border-border text-[9px] px-1.5 py-0.5 rounded shadow-lg whitespace-nowrap"
          style={{
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -100%) translateY(-6px)",
            minWidth: "max-content",
          }}
        >
          <span className="font-semibold text-ink">{fmtDate}</span>
          <span className="text-ink-muted mx-1">·</span>
          <span className="text-accent font-semibold">{count}</span>
          <span className="text-ink-faint"> {getActivityLevel(count)}</span>
          {/* Petite flèche */}
          <div className="absolute left-1/2 -translate-x-1/2 top-full w-1.5 h-1.5 bg-surface border-r border-b border-border rotate-45" />
        </div>
      )}
    </div>
  );
}

export function ProductivityHeatmap({ tasks, events }: ProductivityHeatmapProps) {
  const [viewMode, setViewMode] = useState<ViewMode>("month");

  const gridData = useMemo(() => {
    const dateCountMap = new Map<string, number>();

    for (const task of tasks) {
      if (task.archived) continue;
      const dateKey = toDateStr(new Date(task.createdAt));
      dateCountMap.set(dateKey, (dateCountMap.get(dateKey) ?? 0) + 1);
    }

    for (const event of events) {
      dateCountMap.set(event.date, (dateCountMap.get(event.date) ?? 0) + 1);
    }

    const today = new Date();
    const todayStr = toDateStr(today);
    const startDate = new Date(today);

    if (viewMode === "week") {
      startDate.setDate(today.getDate() - 28);
    } else {
      startDate.setDate(today.getDate() - 90);
    }

    const dow = startDate.getDay();
    startDate.setDate(startDate.getDate() - (dow === 0 ? 6 : dow - 1));

    const weeks: Array<Array<{ date: string; count: number; future: boolean }>> = [];
    const cur = new Date(startDate);

    while (toDateStr(cur) <= todayStr || (weeks.length > 0 && weeks[weeks.length - 1].length < 7)) {
      if (weeks.length === 0 || weeks[weeks.length - 1].length === 7) {
        weeks.push([]);
      }
      const ds = toDateStr(cur);
      weeks[weeks.length - 1].push({
        date: ds,
        count: dateCountMap.get(ds) ?? 0,
        future: ds > todayStr,
      });
      cur.setDate(cur.getDate() + 1);
    }

    const last = weeks[weeks.length - 1];
    while (last.length < 7) {
      const ds = toDateStr(cur);
      last.push({ date: ds, count: 0, future: true });
      cur.setDate(cur.getDate() + 1);
    }

    return weeks;
  }, [tasks, events, viewMode]);

  const monthHeaders = useMemo(() => {
    const result: Array<{ label: string; offset: number }> = [];
    let prev = -1;
    for (let w = 0; w < gridData.length; w++) {
      const m = new Date(gridData[w][0].date + "T00:00:00").getMonth();
      if (m !== prev) {
        result.push({ label: MONTHS[m], offset: w });
        prev = m;
      }
    }
    return result;
  }, [gridData]);

  const getColor = (count: number, future: boolean): string => {
    if (future) return "bg-surface-alt/40";
    if (count === 0) return "bg-surface-alt";
    if (count <= 2) return "bg-emerald-200 dark:bg-emerald-800/40";
    if (count <= 4) return "bg-emerald-400 dark:bg-emerald-700/50";
    if (count <= 6) return "bg-emerald-600 dark:bg-emerald-600";
    return "bg-emerald-700 dark:bg-emerald-500";
  };

  return (
    <div className="bg-surface border border-border rounded-xl overflow-hidden">
      {/* Header avec titre et contrôles */}
      <div className="px-5 py-3 border-b border-border/50">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-ink">Activité</h3>

          <div className="flex items-center bg-surface-alt rounded-lg p-0.5">
            <button
              type="button"
              onClick={() => setViewMode("week")}
              className={`px-3 py-1 text-[10px] font-medium rounded-md transition-colors ${
                viewMode === "week"
                  ? "bg-surface text-ink shadow-sm"
                  : "text-ink-muted hover:text-ink"
              }`}
            >
              4 sem.
            </button>
            <button
              type="button"
              onClick={() => setViewMode("month")}
              className={`px-3 py-1 text-[10px] font-medium rounded-md transition-colors ${
                viewMode === "month"
                  ? "bg-surface text-ink shadow-sm"
                  : "text-ink-muted hover:text-ink"
              }`}
            >
              3 mois
            </button>
          </div>
        </div>

        {/* Légende des couleurs */}
        <div className="flex items-center justify-center gap-4">
          <span className="text-[10px] text-ink-faint">Moins</span>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-[2px] bg-surface-alt" />
              <span className="text-[9px] text-ink-faint">0</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-[2px] bg-emerald-200 dark:bg-emerald-800/40" />
              <span className="text-[9px] text-ink-faint">1-3</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-[2px] bg-emerald-400 dark:bg-emerald-700/50" />
              <span className="text-[9px] text-ink-faint">4-6</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-[2px] bg-emerald-600 dark:bg-emerald-600" />
              <span className="text-[9px] text-ink-faint">7+</span>
            </div>
          </div>
          <span className="text-[10px] text-ink-faint">Plus</span>
        </div>
      </div>

      {/* Grille de chaleur */}
      <div className="overflow-x-auto pb-4">
        <div className="flex justify-center" style={{ gap: `${GAP}px`, paddingTop: `${CELL + 8}px`, paddingBottom: `${CELL + 4}px` }}>
          {/* Day labels column */}
          <div className="flex flex-col shrink-0 sticky left-0 z-10" style={{ gap: `${GAP}px` }}>
            {DAY_LABELS.map((label) => (
              <div
                key={label}
                className="text-[10px] text-ink-muted leading-none flex items-center justify-end pr-2"
                style={{ height: `${CELL}px`, width: 28 }}
              >
                {label}
              </div>
            ))}
          </div>

          {/* Week columns */}
          {gridData.map((week, wIdx) => {
            const mh = monthHeaders.find((m) => m.offset === wIdx);
            return (
              <div key={wIdx} className="relative flex flex-col shrink-0" style={{ gap: `${GAP}px`, width: `${CELL}px` }}>
                {/* Month label */}
                {mh && (
                  <span
                    className="absolute text-[10px] text-ink-muted leading-none whitespace-nowrap"
                    style={{ top: `-${CELL + 4}px`, left: 0 }}
                  >
                    {mh.label}
                  </span>
                )}
                {/* 7 day cells */}
                {week.map((day) => (
                  <HeatmapCell
                    key={day.date}
                    date={day.date}
                    count={day.count}
                    future={day.future}
                    colorClass={getColor(day.count, day.future)}
                  />
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
